// =============================================================================
// MeuDriver — Admin: pedidos LGPD
//
// Antes (até 2026-10-12) o pedido do usuário gravava em lgpd_requests e
// ninguém via. Prazo legal de resposta: 15 dias.
//  * exportação/portabilidade: o próprio usuário baixa os dados em Meu perfil;
//    aqui o admin confirma que atendeu (ou nega com motivo).
//  * correção: o admin corrige em Usuários e marca como atendido.
//  * exclusão: admin_anonymize_user — apaga dados pessoais e bloqueia o login,
//    mantendo os registros financeiros (obrigação legal).
// =============================================================================

import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { differenceInCalendarDays, addDays } from 'date-fns'
import { ShieldCheck, UserX, Check, X } from 'lucide-react'
import { toast } from 'sonner'
import { supabase } from '@/lib/supabase'
import { formatDate } from '@/lib/utils'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import type { LgpdRequest } from '@/hooks/useLgpd'

interface Row extends LgpdRequest {
  user: { full_name: string | null; role: string | null } | null
}

type Action = { kind: 'complete' | 'deny' | 'anonymize'; row: Row }

const TYPE_LABEL: Record<string, string> = {
  export: 'Cópia dos dados',
  portability: 'Portabilidade',
  correction: 'Correção',
  deletion: 'Exclusão',
}

const STATUS_LABEL: Record<string, string> = {
  pending: 'Pendente',
  processing: 'Em andamento',
  completed: 'Atendido',
  denied: 'Negado',
}

const HOW_TO: Record<string, string> = {
  export: 'O usuário baixa os próprios dados em Meu perfil → "Baixar meus dados". Confirme com ele e marque como atendido.',
  portability: 'Mesmo arquivo da cópia dos dados (JSON). Confirme com ele e marque como atendido.',
  correction: 'Corrija o cadastro em Usuários (ou peça ao usuário para editar o perfil) e marque como atendido.',
  deletion: 'Anonimizar apaga nome, telefone, foto, endereços, chave Pix e documentos, troca o e-mail e bloqueia o login. Aulas e pagamentos ficam, sem identificação.',
}

function useAdminLgpd() {
  return useQuery({
    queryKey: ['admin-lgpd'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('lgpd_requests')
        .select('*, user:profiles!lgpd_requests_user_id_fkey(full_name, role)')
        .order('requested_at', { ascending: false })
        .limit(200)
      if (error) throw error
      return (data ?? []) as unknown as Row[]
    },
  })
}

function deadline(row: Row) {
  const due = addDays(new Date(row.requested_at), 15)
  return { due, daysLeft: differenceInCalendarDays(due, new Date()) }
}

export default function AdminLgpd() {
  const qc = useQueryClient()
  const { data: rows = [], isLoading } = useAdminLgpd()
  const [action, setAction] = useState<Action | null>(null)
  const [note, setNote] = useState('')

  const run = useMutation({
    mutationFn: async (a: Action) => {
      if (a.kind === 'anonymize') {
        const { error } = await supabase.rpc('admin_anonymize_user', { p_request_id: a.row.id, p_note: note.trim() || null })
        if (error) throw new Error(error.message)
        return
      }
      const { data: auth } = await supabase.auth.getUser()
      const { error } = await supabase
        .from('lgpd_requests')
        .update({
          status: a.kind === 'complete' ? 'completed' : 'denied',
          notes: note.trim() || null,
          processed_at: new Date().toISOString(),
          processed_by: auth.user?.id ?? null,
        })
        .eq('id', a.row.id)
      if (error) throw new Error(error.message)
    },
    onSuccess: (_, a) => {
      toast.success(a.kind === 'anonymize' ? 'Dados do usuário removidos.' : a.kind === 'complete' ? 'Pedido marcado como atendido.' : 'Pedido negado.')
      setAction(null)
      qc.invalidateQueries({ queryKey: ['admin-lgpd'] })
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Erro ao processar o pedido'),
  })

  function start(a: Action) {
    setNote('')
    setAction(a)
  }

  function confirm() {
    if (!action) return
    if (action.kind === 'deny' && note.trim().length < 5) {
      toast.error('Explique o motivo da negativa (o usuário recebe esse texto).')
      return
    }
    run.mutate(action)
  }

  const open = rows.filter((r) => r.status === 'pending' || r.status === 'processing')
  const closed = rows.filter((r) => r.status === 'completed' || r.status === 'denied')

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center gap-3">
        <ShieldCheck className="h-6 w-6 text-gray-500" />
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Pedidos LGPD</h1>
          <p className="text-sm text-muted-foreground">
            Pedidos dos usuários sobre os próprios dados. Prazo legal de resposta: 15 dias.
          </p>
        </div>
      </div>

      <Card>
        <CardHeader className="border-b pb-4">
          <CardTitle className="flex items-center gap-2">
            Em aberto {open.length > 0 && <Badge variant="destructive">{open.length}</Badge>}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="space-y-3 p-4">{[0, 1].map((i) => <Skeleton key={i} className="h-16 w-full" />)}</div>
          ) : open.length === 0 ? (
            <p className="py-10 text-center text-sm text-muted-foreground">Nenhum pedido em aberto.</p>
          ) : (
            <div className="divide-y">
              {open.map((r) => {
                const { due, daysLeft } = deadline(r)
                return (
                  <div key={r.id} className="space-y-3 p-4">
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge variant="outline">{TYPE_LABEL[r.request_type] ?? r.request_type}</Badge>
                      <Link to={`/admin/usuarios/${r.user_id}`} className="font-medium text-brand-700 hover:underline">
                        {r.user?.full_name ?? 'Usuário'}
                      </Link>
                      <span className="text-xs text-muted-foreground">pedido em {formatDate(r.requested_at, 'dd/MM/yyyy')}</span>
                      <Badge className={daysLeft <= 3 ? 'bg-red-100 text-red-800 border-0' : 'bg-gray-100 text-gray-700 border-0'}>
                        {daysLeft >= 0 ? `prazo ${formatDate(due.toISOString(), 'dd/MM')} (${daysLeft} dia${daysLeft === 1 ? '' : 's'})` : 'prazo vencido'}
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground">{HOW_TO[r.request_type]}</p>
                    <div className="flex flex-wrap gap-2">
                      {r.request_type === 'deletion' ? (
                        <Button size="sm" className="gap-1 bg-red-600 hover:bg-red-700" onClick={() => start({ kind: 'anonymize', row: r })}>
                          <UserX className="h-3.5 w-3.5" /> Anonimizar usuário
                        </Button>
                      ) : (
                        <Button size="sm" className="gap-1" onClick={() => start({ kind: 'complete', row: r })}>
                          <Check className="h-3.5 w-3.5" /> Marcar como atendido
                        </Button>
                      )}
                      <Button size="sm" variant="outline" className="gap-1" onClick={() => start({ kind: 'deny', row: r })}>
                        <X className="h-3.5 w-3.5" /> Negar
                      </Button>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {closed.length > 0 && (
        <Card>
          <CardHeader className="border-b pb-4"><CardTitle>Encerrados</CardTitle></CardHeader>
          <CardContent className="divide-y p-0">
            {closed.map((r) => (
              <div key={r.id} className="flex flex-col gap-1 p-4 text-sm sm:flex-row sm:items-center sm:justify-between">
                <span>
                  <Badge variant="outline" className="mr-2">{TYPE_LABEL[r.request_type] ?? r.request_type}</Badge>
                  {r.user?.full_name ?? 'Usuário'}
                  {r.notes && <span className="text-muted-foreground"> — {r.notes}</span>}
                </span>
                <span className="text-xs text-muted-foreground">
                  {STATUS_LABEL[r.status]} {r.processed_at ? `em ${formatDate(r.processed_at, 'dd/MM/yyyy')}` : ''}
                </span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <Dialog open={!!action} onOpenChange={(o) => { if (!o && !run.isPending) setAction(null) }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {action?.kind === 'anonymize' ? 'Anonimizar usuário' : action?.kind === 'complete' ? 'Marcar como atendido' : 'Negar pedido'}
            </DialogTitle>
            <DialogDescription>
              {action?.kind === 'anonymize'
                ? `Os dados pessoais de ${action.row.user?.full_name ?? 'usuário'} serão apagados e o login bloqueado. Não dá para desfazer.`
                : 'O texto abaixo é enviado ao usuário.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="lgpd-note">{action?.kind === 'deny' ? 'Motivo (obrigatório)' : 'Observação (opcional)'}</Label>
            <Textarea id="lgpd-note" rows={3} maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} />
          </div>
          <DialogFooter>
            <Button variant="outline" disabled={run.isPending} onClick={() => setAction(null)}>Voltar</Button>
            <Button disabled={run.isPending} onClick={confirm}
              className={action?.kind === 'anonymize' ? 'bg-red-600 hover:bg-red-700' : undefined}>
              {run.isPending ? 'Processando…' : 'Confirmar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
